import { Select, Spin } from 'antd';
import React, { useMemo, useState, useEffect } from 'react';
import { useGetNoteTypesQuery } from '@ui/app/api';
import type { IngestionFilterCondition } from '../../types';

interface IngestionNoteTypeSelectProps {
    condition: IngestionFilterCondition;
    groupId: string;
    updateCondition: (groupId: string, conditionId: string, updates: Partial<IngestionFilterCondition>) => void;
}

/**
 * Multi-select for note types in ingestion filter
 * Fetches unique document types from Elasticsearch
 */
export const IngestionNoteTypeSelect: React.FC<IngestionNoteTypeSelectProps> = ({
    condition,
    groupId,
    updateCondition,
}) => {
    const [noteTypeSearch, setNoteTypeSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(noteTypeSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [noteTypeSearch]);

    const { data, isLoading: isLoadingNoteTypes } = useGetNoteTypesQuery({
        search: debouncedSearch,
        limit: 100,
    });

    const noteTypes = data?.noteTypes || [];

    const noteTypeOptions = useMemo(
        () =>
            noteTypes.map(type => ({
                label: type,
                value: type,
            })),
        [noteTypes]
    );

    const handleChange = (vals: string[] | undefined) => {
        updateCondition(groupId, condition.id, {
            value: vals && vals.length > 0 ? vals : undefined,
        });
    };

    return (
        <div className="flex w-full flex-col gap-2">
            <Select
                mode="multiple"
                placeholder="Select note types..."
                className="w-full"
                allowClear
                showSearch
                filterOption={true}
                onSearch={setNoteTypeSearch}
                options={noteTypeOptions}
                loading={isLoadingNoteTypes}
                value={condition.value}
                onChange={handleChange}
                notFoundContent={isLoadingNoteTypes ? <Spin size="small" /> : 'No note types found'}
                maxTagCount="responsive"
            />
        </div>
    );
};
